import type { ReactNode } from "react";
import { Box } from "./box";
import { CopyTextButton } from "./copy";
import { CostEstimateTooltip } from "./cost-estimate-tooltip";
import { DetailLabel } from "./detail-label";
import { LastDeployAtDisplay } from "./last-deploy-at";
import { LoadingSpinner } from "./loading";
import { tokens } from "./tokens";

export interface ResourceDetailBarProps {
  icon?: ReactNode;
  nameLabel?: string;
  name: string;
  nameCopyText?: string;
  id: string;
  url?: ReactNode;
  dateLabel?: string;
  /** Pass null while the resource this date comes from is still loading (e.g. a release fetched separately from its service) to show a spinner instead of formatting an empty string. */
  createdAt: string | null;
  costLabel?: string;
  cost?: number | null;
  costTooltip?: string;
  isLoading?: boolean;
}

export function BarColumn({
  icon,
  label,
  tooltip,
  isLoading = false,
  spanFull = false,
  copyText,
  children,
}: {
  icon?: ReactNode;
  label: string;
  tooltip?: string;
  isLoading?: boolean;
  spanFull?: boolean;
  copyText?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`min-w-0 px-4 py-3 first:pl-4 flex items-center gap-2 xl:flex-1 ${spanFull ? "sm:col-span-2" : ""}`}
    >
      {icon ? <div className="w-[32px] h-[32px] shrink-0">{icon}</div> : null}
      <div className="min-w-0">
        <DetailLabel label={label} tooltip={tooltip} isLoading={isLoading} />
        <div className="mt-1 min-w-0">
          {copyText ? (
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`${tokens.type["detail value"]} break-words min-w-0`}
              >
                {children}
              </div>
              <CopyTextButton text={copyText} className="shrink-0" />
            </div>
          ) : (
            <div className={`${tokens.type["detail value"]} break-words`}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const BAR_COLUMNS_CLASS =
  "flex flex-col divide-y divide-black-100 sm:grid sm:grid-cols-2 sm:divide-y-0 sm:[&>*:nth-child(2n)]:border-l sm:[&>*:nth-child(2n)]:border-black-100 sm:max-xl:[&>*:nth-child(n+3)]:!border-t sm:max-xl:[&>*:nth-child(n+3)]:!border-black-100 xl:flex xl:flex-row xl:items-stretch xl:divide-x xl:divide-black-100";

export function ResourceDetailBar({
  icon,
  nameLabel = "Name",
  name,
  nameCopyText,
  id,
  url,
  dateLabel = "Created",
  createdAt,
  costLabel = "Est. Monthly Cost",
  cost,
  costTooltip,
  isLoading = false,
}: ResourceDetailBarProps) {
  return (
    <Box size="none">
      <div className={BAR_COLUMNS_CLASS}>
        <BarColumn
          icon={icon}
          label={nameLabel}
          isLoading={isLoading}
          copyText={nameCopyText}
        >
          {name}
        </BarColumn>
        <BarColumn label="ID" isLoading={isLoading}>
          {id}
        </BarColumn>
        {url != null ? (
          <BarColumn label="URL" isLoading={isLoading}>
            {url}
          </BarColumn>
        ) : null}
        <BarColumn label={dateLabel} isLoading={isLoading}>
          {createdAt == null ? (
            <LoadingSpinner />
          ) : (
            <LastDeployAtDisplay at={createdAt} />
          )}
        </BarColumn>
        <BarColumn
          label={costLabel}
          tooltip={costTooltip}
          isLoading={isLoading}
          spanFull={url != null}
        >
          <CostEstimateTooltip cost={cost ?? null} />
        </BarColumn>
      </div>
    </Box>
  );
}
